/** @class StyleProvider
 *  @description Component for creation default styles in widget, useful for BO in preview and for development widget.
 */
import axios from 'axios';
import * as React from 'react';
import * as URI from 'urijs';
// utils
import {
    ASSET_MANIFEST_URL,
    createDefaultStyleFileName,
    createLinkElement,
    CURRENT_LOCATION,
    customCSSSelector
} from './style-provider.utils';
// styles
import 'styles/root.less';
import { CUSTOM_STYLES_ID } from '@sitegen/common/constants/styles';
// constants
import { IStyles } from '@sitegen/common/constants/config';
import { WIDGET_NAME, WIDGET_CONFIG, WIDGET_TYPE } from '@sitegen/common/constants';
// types
import { IAssetManifest } from '@sitegen/common/constants/asset-manifest';

export default class StyleProvider extends React.Component<IStyleProviderProps, IStyleProviderState> {
    private styles: Array<HTMLLinkElement | HTMLStyleElement> = [];

    private targetDOMNode: HTMLDivElement;
    private rootElementRefHandler = (ref: HTMLDivElement) => this.targetDOMNode = ref;

    public state: IStyleProviderState = {
        loading: true
    };

    public componentDidMount(): void {
        this.applyStyles().finally(() => {
            this.setState({ loading: false });
        });
    }

    public componentWillUnmount(): void {
        this.removeStyles();
    }

    public render(): JSX.Element | null {
        return (
            <div className="sitegen-widget" ref={this.rootElementRefHandler}>
                {this.state.loading ? null : this.props.children}
            </div>
        );
    }

    private applyStyles(): Promise<void | Array<void>> {
        const areStylesAlreadyApplied: boolean = this.props.styles!.areStylesAlreadyApplied;

        if (areStylesAlreadyApplied) {
            return Promise.resolve();
        }

        return Promise.all([
            this.applyDefaultStyles(),
            this.applyCustomStyles(),
            this.applyAssetsStyles()
        ]);
    }

    private removeStyles(): void {
        this.styles.forEach(element => element.remove());
        this.styles.splice(0, this.styles.length);
    }

    private applyColorsAndStyling = (): void => {
        if (!this.props.styles.colorsAndStylingContent) {
            return;
        }

        const el: HTMLStyleElement = document.createElement('style');
        el.innerText = this.props.styles.colorsAndStylingContent;

        this.styles.push(el);
        this.targetDOMNode!.appendChild(el);
    };

    private applyDefaultStyles = async (): Promise<void> => {
        if (!CURRENT_LOCATION) {
            return Promise.resolve();
        }

        let defaultStylePaths: string[];

        try {
            defaultStylePaths = await this.getDefaultStylePaths();
        } catch (error) {
            return Promise.reject(error.message);
        }

        await Promise.all(
            defaultStylePaths.map(this.applyDefaultStyle)
        );

        /* Append colors variables after default styles are set! */
        this.applyColorsAndStyling();
    };

    private applyCustomStyles = (): Promise<void> => {
        return this.createStyleLink(this.props.styles.custom, CUSTOM_STYLES_ID);
    };

    private applyAssetsStyles(): Promise<void> {
        return this.createStyleLink(this.props.styles.assets);
    }

    private applyDefaultStyle = (stylePath: string): Promise<void> => {
        return new Promise((resolve, reject): void => {
            const linkElement: HTMLLinkElement = createLinkElement({ url: stylePath });
            linkElement.onload = () => resolve();
            linkElement.onerror = error => reject(error);

            // Trying to find custom.css in document for standalone mode and in targetDOMNode for embedded.
            const customStyles: HTMLElement | null =
                document.querySelector(customCSSSelector) || this.targetDOMNode.querySelector(customCSSSelector);

            if (customStyles && customStyles.parentNode) {
                customStyles.parentNode.insertBefore(linkElement, customStyles);
            } else {
                this.targetDOMNode!.appendChild(linkElement);
            }

            this.styles.push(linkElement);
        });
    };

    private getDefaultStylePaths = async (): Promise<string[]> => {
        let manifest: IAssetManifest;

        try {
            const response = await axios.get<IAssetManifest>(ASSET_MANIFEST_URL);
            manifest = response.data;
        } catch (error) {
            throw new Error(`Can't load asset-manifest.json file: ${error.message}`);
        }

        /* Styles are based on widget type rather than on widget name */
        const widgetTypes: WIDGET_TYPE[] = this.props.widgetNames
            .map((name: WIDGET_NAME): WIDGET_TYPE => {
                return WIDGET_CONFIG[name].widgetType;
            });

        /* Load CSS code based on widget type */
        return widgetTypes.map((widgetType: WIDGET_TYPE): string => {
            const fileKey: string = createDefaultStyleFileName(widgetType);
            const filePath: string = manifest[fileKey];
            return new URI(CURRENT_LOCATION || '').segment(filePath).href();
        });
    };

    private createStyleLink = (url?: string, id?: string): Promise<void> => {
        if (!url) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const linkElement: HTMLLinkElement = createLinkElement({ url, id });
            linkElement.onload = () => resolve();
            linkElement.onerror = error => reject(error);
            this.targetDOMNode!.appendChild(linkElement);
            this.styles.push(linkElement);
        });
    };
}

interface IStyleProviderState {
    readonly loading: boolean | null;
}

interface IStyleProviderProps {
    readonly widgetNames: WIDGET_NAME[];
    readonly styles: IStyles;
}
